import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Edit, Trash2, ChevronDown, Upload, Sparkles, Loader } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { mockCategories } from '../../data/mockData';
import { FirestoreService } from '../../firebase/firestore';

const AdminProduct: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { products, updateProducts, categories } = useData();
  
  // Debug: Log categories to console
  console.log('Categories loaded:', categories);
  
  // Use categories from context, fallback to mockCategories if empty
  const availableCategories = categories.length > 0 ? categories : mockCategories;
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    category: '',
    image: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  const generateAIImage = async () => {
    if (!newProduct.name || !newProduct.description) {
      alert('Please enter product name and description first');
      return;
    }

    setIsGeneratingImage(true);
    try {
      // Test with a simple prompt first to verify Pollinations.ai is working
      const testPrompt = 'cement bag construction material';
      const testUrl = `https://pollinations.ai/p/${encodeURIComponent(testPrompt)}?width=512&height=512&nologo=true`;
      console.log('🧪 Testing Pollinations.ai with URL:', testUrl);
      
      // Create a more detailed and specific prompt for construction materials
      const basePrompt = `${newProduct.name}`;
      const description = newProduct.description.toLowerCase();
      
      // Add specific keywords based on product type
      let styleKeywords = '';
      if (description.includes('cement') || description.includes('concrete')) {
        styleKeywords = 'cement bag, construction material, industrial product, building material, cement packaging, 50kg bag, professional product photography, white background, studio lighting';
      } else if (description.includes('brick') || description.includes('block')) {
        styleKeywords = 'red brick, construction material, building block, masonry, clay brick, industrial product, professional product photography, white background, studio lighting';
      } else if (description.includes('steel') || description.includes('metal')) {
        styleKeywords = 'steel rod, construction material, metal, industrial product, building material, steel bar, professional product photography, white background, studio lighting';
      } else if (description.includes('sand') || description.includes('gravel')) {
        styleKeywords = 'construction aggregate, sand, gravel, building material, industrial product, professional product photography, white background, studio lighting';
      } else {
        styleKeywords = 'construction material, building product, industrial product, professional product photography, white background, studio lighting';
      }
      
      const fullPrompt = `${basePrompt}, ${newProduct.description}, ${styleKeywords}, high resolution, detailed, commercial product photo, product packaging, construction industry`;
      const encodedPrompt = encodeURIComponent(fullPrompt);
      
      console.log('🎨 Generating AI image with prompt:', fullPrompt);
      
      // Pollinations.ai with correct URL format
      const pollinationsUrl = `https://pollinations.ai/p/${encodedPrompt}?width=512&height=512&nologo=true&seed=${Date.now()}`;
      console.log('🎨 Generated Pollinations.ai URL:', pollinationsUrl);
      
      // Try multiple approaches to load the image
      const corsProxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(pollinationsUrl)}`;
      const corsProxyUrl2 = `https://cors-anywhere.herokuapp.com/${pollinationsUrl}`;
      
      console.log('🔗 CORS proxy URL 1:', corsProxyUrl);
      console.log('🔗 CORS proxy URL 2:', corsProxyUrl2);
      console.log('🔗 Direct Pollinations.ai URL:', pollinationsUrl);
      
      // Set the image using first CORS proxy
      setNewProduct({ ...newProduct, image: corsProxyUrl });
      setImagePreview(corsProxyUrl);
      console.log('✅ AI image URL set successfully using CORS proxy');
      
      // Test if the image loads properly with fallbacks
      const testImage = new window.Image();
      testImage.onload = () => {
        console.log('✅ Pollinations.ai image loaded successfully via CORS proxy');
      };
      testImage.onerror = () => {
        console.log('⚠️ CORS proxy 1 failed, trying CORS proxy 2');
        setNewProduct({ ...newProduct, image: corsProxyUrl2 });
        setImagePreview(corsProxyUrl2);
        
        // Test second proxy
        const testImage2 = new window.Image();
        testImage2.onload = () => {
          console.log('✅ Pollinations.ai image loaded successfully via CORS proxy 2');
        };
        testImage2.onerror = () => {
          console.log('⚠️ CORS proxy 2 failed, using direct URL');
          setNewProduct({ ...newProduct, image: pollinationsUrl });
          setImagePreview(pollinationsUrl);
        };
        testImage2.src = corsProxyUrl2;
      };
      testImage.src = corsProxyUrl;
      
    } catch (error) {
      console.error('Error generating AI image:', error);
      alert('Failed to generate AI image using Pollinations.ai. Please check your internet connection and try again, or upload an image manually.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        setNewProduct({ ...newProduct, image: imageUrl });
        setImagePreview(imageUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price || !newProduct.stock || !newProduct.category) {
      alert('Please fill in all required fields including category');
      return;
    }
    
    const product = {
      name: newProduct.name,
      description: newProduct.description,
      price: parseFloat(newProduct.price),
      stock: parseInt(newProduct.stock),
      category: newProduct.category,
      brand: 'BookFood',
      image: newProduct.image || 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=200&h=200&fit=crop'
    };
    
    try {
      console.log('💾 Saving product to Firestore...', product);
      const productId = await FirestoreService.addProduct(product);
      
      if (productId) {
        console.log('✅ Product saved to Firestore with ID:', productId);
        // Add the product with the Firestore ID to local state
        const productWithId = { ...product, id: productId };
        updateProducts([...products, productWithId]);
        setNewProduct({ name: '', description: '', price: '', stock: '', category: '', image: '' });
        setImagePreview('');
        alert('Product added successfully!');
      } else {
        console.error('❌ Failed to save product to Firestore');
        alert('Failed to save product. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error saving product:', error);
      alert('Error saving product. Please try again.');
    }
  };

  const handleEditProduct = (product: any) => {
    setEditingProduct(product);
    setNewProduct({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      stock: product.stock.toString(),
      category: product.category,
      image: product.image
    });
    setImagePreview(product.image);
  };

  const handleUpdateProduct = async () => {
    if (!newProduct.name || !newProduct.price || !newProduct.stock || !newProduct.category) {
      alert('Please fill in all required fields including category');
      return;
    }
    
    const updatedProduct = {
      name: newProduct.name,
      description: newProduct.description,
      price: parseFloat(newProduct.price),
      stock: parseInt(newProduct.stock),
      category: newProduct.category,
      image: newProduct.image || editingProduct.image
    };
    
    try {
      console.log('💾 Updating product in Firestore...', updatedProduct);
      const success = await FirestoreService.updateProduct(editingProduct.id, updatedProduct);
      
      if (success) {
        console.log('✅ Product updated in Firestore');
        const updatedProducts = products.map(p => 
          p.id === editingProduct.id 
            ? { ...p, ...updatedProduct }
            : p
        );
        updateProducts(updatedProducts);
        setEditingProduct(null);
        setNewProduct({ name: '', description: '', price: '', stock: '', category: '', image: '' });
        setImagePreview('');
        alert('Product updated successfully!');
      } else {
        console.error('❌ Failed to update product in Firestore');
        alert('Failed to update product. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error updating product:', error);
      alert('Error updating product. Please try again.');
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        console.log('🗑️ Deleting product from Firestore...', productId);
        const success = await FirestoreService.deleteProduct(productId);
        
        if (success) {
          console.log('✅ Product deleted from Firestore');
          updateProducts(products.filter(p => p.id !== productId));
          alert('Product deleted successfully!');
        } else {
          console.error('❌ Failed to delete product from Firestore');
          alert('Failed to delete product. Please try again.');
        }
      } catch (error) {
        console.error('❌ Error deleting product:', error);
        alert('Error deleting product. Please try again.');
      }
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const ProductCard: React.FC<{ product: any; index: number }> = ({ product, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-slate-800 rounded-lg p-4 border border-slate-700"
    >
      <div className="flex items-center space-x-4">
        <img
          src={product.image}
          alt={product.name}
          className="w-16 h-16 rounded-lg object-cover"
        />
        <div className="flex-1">
          <h3 className="text-white font-medium">{product.name}</h3>
          <p className="text-slate-400 text-sm">{product.brand}</p>
          <p className="text-purple-400 text-xs">{product.category}</p>
          <p className="text-teal-400 font-semibold">₹{product.price}</p>
          <p className="text-slate-500 text-xs">Stock: {product.stock}</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditProduct(product)}
            className="p-2 text-blue-400 hover:text-blue-300"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDeleteProduct(product.id)}
            className="p-2 text-red-400 hover:text-red-300"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center p-6 bg-slate-800 border-b border-slate-700">
        <button
          onClick={handleBack}
          className="p-2 text-slate-400 hover:text-white mr-4"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-white">Product Management</h1>
      </div>

      <div className="p-6 space-y-6">
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Search products..."
          />
        </motion.div>

        {/* Add/Edit Product */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800 rounded-xl p-4 border border-slate-700"
        >
          <h2 className="text-lg font-semibold text-white mb-4">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="e.g., Ultra-Strength Cement"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Product Description
              </label>
              <textarea
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="Describe the product features"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Category * ({availableCategories.length} available)
              </label>
              <div className="relative">
                <select
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select Category</option>
                  {availableCategories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              </div>
              {/* Debug info */}
              <div className="text-xs text-slate-500 mt-1">
                Debug: {availableCategories.length} categories loaded
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Price *
                </label>
                <input
                  type="number"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="₹0.00"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Stock *
                </label>
                <input
                  type="number"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Image Upload Section */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Product Image
              </label>
              
              {/* Image Preview */}
              {(imagePreview || newProduct.image) && (
                <div className="mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-32 h-32 rounded-lg border border-slate-600 bg-slate-800 flex items-center justify-center relative">
                      {isGeneratingImage ? (
                        <div className="text-center">
                          <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                          <div className="text-slate-400 text-xs">Generating...</div>
                        </div>
                      ) : (
                        <img
                          src={imagePreview || newProduct.image}
                          alt="Product preview"
                          className="w-full h-full object-cover rounded-lg"
                          onLoad={() => console.log('✅ Image preview loaded')}
                          onError={(e) => {
                            console.error('❌ Image preview failed to load:', e);
                            console.log('🔗 Failed URL:', imagePreview || newProduct.image);
                            // Show a fallback message
                            const img = e.target as HTMLImageElement;
                            img.style.display = 'none';
                            const parent = img.parentElement;
                            if (parent) {
                              parent.innerHTML = `
                                <div class="text-center p-4">
                                  <div class="text-slate-400 text-xs mb-2">Image Loading...</div>
                                  <div class="text-slate-500 text-xs break-all">${imagePreview || newProduct.image}</div>
                                  <div class="text-slate-500 text-xs mt-2">Click to open in new tab</div>
                                </div>
                              `;
                              parent.onclick = () => window.open(imagePreview || newProduct.image, '_blank');
                              parent.style.cursor = 'pointer';
                            }
                          }}
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-400">Image Preview:</p>
                        <motion.button
                          onClick={generateAIImage}
                          disabled={isGeneratingImage || !newProduct.name || !newProduct.description}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center space-x-1 px-2 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white text-xs rounded transition-colors duration-200 disabled:cursor-not-allowed"
                        >
                          {isGeneratingImage ? (
                            <Loader size={12} className="animate-spin" />
                          ) : (
                            <Sparkles size={12} />
                          )}
                          <span>{isGeneratingImage ? 'Regenerating...' : 'Regenerate'}</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Image Upload Options */}
              <div className="space-y-3">
                {/* Upload from Device */}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white hover:bg-slate-600 transition-colors duration-200 cursor-pointer"
                  >
                    <Upload size={20} />
                    <span>Upload Image</span>
                  </label>
                </div>

                {/* Generate AI Image */}
                <motion.button
                  onClick={generateAIImage}
                  disabled={isGeneratingImage || !newProduct.name || !newProduct.description}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-slate-600 disabled:to-slate-600 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
                >
                  {isGeneratingImage ? (
                    <>
                      <Loader size={20} className="animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      <span>Generate AI Image</span>
                    </>
                  )}
                </motion.button>

              </div>
            </div>

            <div className="flex space-x-3">
              <motion.button
                onClick={editingProduct ? handleUpdateProduct : handleAddProduct}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                {editingProduct ? 'Update Product' : 'Add Product'}
              </motion.button>
              {editingProduct && (
                <motion.button
                  onClick={() => {
                    setEditingProduct(null);
                    setNewProduct({ name: '', description: '', price: '', stock: '', category: '', image: '' });
                    setImagePreview('');
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-4 bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Existing Products */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">
            Products ({filteredProducts.length})
          </h2>
          <div className="grid grid-cols-1 gap-4">
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-400">No products found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default AdminProduct;
